import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icons as I } from '../pack-icons';
import { api } from '../../services/api/client.js';
import { mcpOnboardingEnabled } from '../../lib/flags.js';
import {
  MCP_ONBOARDING_HANDOFF_DONE_KEY,
  MCP_ONBOARDING_STARTED_KEY,
  readMcpOnboardingChoice,
  writeMcpOnboardingChoice,
} from '../../lib/connectors.js';
import { StarterPrompt } from '../../screens/OnboardingMcpShared.jsx';
import { useDashboardData } from '../../state/DashboardDataContext.jsx';
import { isPostingPlan } from '../../lib/finishSetup.js';

const POLL_MS = 15000;
const RESEARCH_ACTIVE = ['queued', 'running', 'gathering', 'analyzing', 'synthesizing', 'verifying'];

function readLocal(key) {
  try { return window.localStorage.getItem(key); } catch (_) { return null; }
}

function writeLocal(key, value) {
  try { window.localStorage.setItem(key, value); } catch (_) { /* private mode */ }
}

export default function AiSetupWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dash = useDashboardData() || {};
  // Owner decision 2026-09-07 (item 4): the MCP "ai" onboarding choice
  // (writeMcpOnboardingChoice('ai')) is recorded BEFORE plan selection, so a
  // user who picked the AI chat path there but later bought Scheduler
  // (posting has no research/strategy pipeline) would otherwise keep seeing
  // this handoff card stuck incomplete forever. planKnown-gated so it never
  // flashes for a tick before entitlements resolve either.
  const planKnown = Boolean(dash.fetchedAt?.entitlements);
  const posting = isPostingPlan(dash.entitlements);
  const [engagedBase] = useState(() => mcpOnboardingEnabled()
    && readMcpOnboardingChoice() === 'ai'
    && Boolean(readLocal(MCP_ONBOARDING_STARTED_KEY))
    && readLocal(MCP_ONBOARDING_HANDOFF_DONE_KEY) !== '1');
  const engaged = engagedBase && planKnown && !posting;
  const [loaded, setLoaded] = useState(false);
  const [brandReady, setBrandReady] = useState(false);
  const [researchStatus, setResearchStatus] = useState(null);
  const [planReady, setPlanReady] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  const load = useCallback(async () => {
    if (document.visibilityState !== 'visible') return;
    api.invalidateCache?.('/api/brand-profile');
    api.invalidateCache?.('/api/research');
    api.invalidateCache?.('/api/strategy');
    const [brand, research, strategy] = await Promise.allSettled([
      api.profile.getBrandProfile(),
      api.research.list(),
      api.strategy.get(),
    ]);
    if (brand.status === 'fulfilled') setBrandReady(Boolean(brand.value?.brandProfile || brand.value?.data?.brandProfile || brand.value));
    if (research.status === 'fulfilled') {
      const payload = research.value?.data ?? research.value;
      setResearchStatus(payload?.reports?.[0]?.status || null);
    }
    if (strategy.status === 'fulfilled') {
      const payload = strategy.value?.data ?? strategy.value;
      setPlanReady(Boolean(payload?.strategy));
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!engaged) return undefined;
    load();
    const timer = setInterval(load, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [engaged, load]);

  const researchReady = researchStatus === 'ready';
  const researchRunning = RESEARCH_ACTIVE.includes(researchStatus);
  const complete = brandReady && researchReady && planReady;
  useEffect(() => {
    if (complete) writeLocal(MCP_ONBOARDING_HANDOFF_DONE_KEY, '1');
  }, [complete]);

  if (!engaged || !loaded) return null;

  const steps = [
    { key: 'site', state: brandReady ? 'ok' : 'wait', label: t(brandReady ? 'mcpOnboarding.handoffSiteDone' : 'mcpOnboarding.handoffSitePending') },
    { key: 'research', state: researchReady ? 'ok' : researchRunning ? 'run' : 'wait', label: t(researchReady ? 'mcpOnboarding.handoffResearchDone' : researchRunning ? 'mcpOnboarding.handoffResearchRunning' : 'mcpOnboarding.handoffResearchPending') },
    { key: 'plan', state: planReady ? 'ok' : 'wait', label: t(planReady ? 'mcpOnboarding.handoffPlanDone' : 'mcpOnboarding.handoffPlanPending') },
  ];
  const doneCount = steps.filter((step) => step.state === 'ok').length;

  const finishHere = () => {
    writeMcpOnboardingChoice('ui');
    navigate('/onboarding');
  };

  return (
    <section className={`mcpo-handoff ${complete ? 'mcpo-is-complete' : ''}`} role="status">
      <span className="mcpo-handoff-mark">{complete ? <I.check size={19} /> : <I.message size={19} />}</span>
      <div className="mcpo-handoff-body">
        <h3>{t(complete ? 'mcpOnboarding.handoffCompleteTitle' : 'mcpOnboarding.handoffTitle')} <span className="mcpo-handoff-progress">· {doneCount} {t('mcpOnboarding.handoffOfThree')}</span></h3>
        <p className="mcpo-handoff-subtitle">{t(complete ? 'mcpOnboarding.handoffCompleteSubtitle' : 'mcpOnboarding.handoffSubtitle')}</p>
        {!complete && (
          <div className="mcpo-handoff-ticks">
            {steps.map((step) => (
              <span key={step.key} className={`mcpo-handoff-tick mcpo-is-${step.state}`}>
                <span className="mcpo-handoff-dot">{step.state === 'ok' ? <I.check size={10} /> : step.state === 'run' ? <I.refresh size={14} /> : null}</span>{step.label}
              </span>
            ))}
          </div>
        )}
        {showPrompt && <div className="mcpo-handoff-prompt"><StarterPrompt caption={false} /></div>}
      </div>
      <div className="mcpo-handoff-actions">
        {complete ? (
          <>
            <button type="button" className="mcpo-handoff-primary" onClick={() => navigate('/app/plan')}>{t('mcpOnboarding.handoffSeePlan')}</button>
            <button type="button" className="mcpo-handoff-ghost" onClick={() => setShowPrompt((open) => !open)}><I.message size={13} />{t('mcpOnboarding.handoffBackChat')}</button>
          </>
        ) : (
          <>
            <button type="button" className="mcpo-handoff-primary" onClick={() => setShowPrompt((open) => !open)} aria-expanded={showPrompt}><I.message size={13} />{t('mcpOnboarding.handoffResume')}</button>
            <button type="button" className="mcpo-handoff-ghost" onClick={finishHere}>{t('mcpOnboarding.handoffFinishHere')}</button>
          </>
        )}
      </div>
    </section>
  );
}
